import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { InversionistasService } from '../../../services/inversionistas.service';
import { HelpersService } from '../../../services/tools/helpers.service';

declare var $: any;

@Component({
  selector: 'app-pagare-detalles',
  templateUrl: './pagare-detalles.component.html',
  styleUrls: ['./pagare-detalles.component.scss']
})
export class PagareDetallesComponent implements OnInit, OnDestroy {
  public idPagare: number;
  public pagare: any = {};
  public loading: boolean = true;
  public seccionActiva: string = 'datosGenerales';
  public movimientoForm: FormGroup;

  // Subject para cancelar las suscripciones al destruir el componente
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private inversionistasService: InversionistasService,
    private helperService: HelpersService
  ) { }

  ngOnInit() {
    this.inicializarFormulario();
    this.escucharCambiosFormulario();

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.idPagare = +params['id'];
      if (this.idPagare) {
        this.getDetallePagare(this.idPagare);
      }
    });
  }

  ngOnDestroy(): void {
    // Limpieza de memoria de todas las suscripciones activas
    this.destroy$.next();
    this.destroy$.complete();
  }

  public getDetallePagare(id: number) {
    this.loading = true;
    this.inversionistasService.obtenerDetallePagare(id).pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (response: any) => {
        const result = response.json ? response.json() : response;
        if (result.success) {
          this.pagare = result.content;
        }
        this.loading = false;
      },
      error => {
        this.loading = false;
        this.helperService.httpGlobalError(error);
      }
    );
  }

  public cambiarSeccion(seccion: string) {
    this.seccionActiva = seccion;
  }

  public volver() {
    window.history.back();
  }

  private inicializarFormulario(): void {
    this.movimientoForm = this.fb.group({
      folio: [''],
      divisa: [''],
      inversionista: [''],
      tipo_movimiento: ['', Validators.required],
      fecha: ['', Validators.required],
      intereses: [''],
      saldo: [''],
      monto: ['', Validators.required],
      penalizacion: [0],
      programar_movimiento: [false],
      nuevo_monto_capital: ['No Capturado'],
      nuevo_monto_intereses: ['No Capturado']
    });
  }

  private escucharCambiosFormulario(): void {
    // 1. Tipo de movimiento y fecha
    this.movimientoForm.get('tipo_movimiento').valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => this.verificarYCalcularPrevia());

    this.movimientoForm.get('fecha').valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => this.verificarYCalcularPrevia());

    // 2. Monto con debounce, distinct y control de destrucción
    this.movimientoForm.get('monto').valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(monto => {
      if (monto && monto > 0) {
        this.calcularNuevosMontos(monto);
      } else {
        this.movimientoForm.patchValue({
          nuevo_monto_capital: null,
          nuevo_monto_intereses: null
        }, { emitEvent: false });
      }
    });
  }

private verificarYCalcularPrevia(): void {
    const tipo = this.movimientoForm.get('tipo_movimiento').value;
    const fecha = this.movimientoForm.get('fecha').value;

    if (tipo && fecha && this.idPagare) {
      this.inversionistasService.calcularPreviaMovimiento(this.idPagare, { tipo_movimiento: tipo, fecha }).pipe(
        takeUntil(this.destroy$)
      ).subscribe(
        (response: any) => {
          const res = response.json ? response.json() : response;
          if (res.success && res.content) {
            this.movimientoForm.patchValue({
              intereses: res.content.intereses || 0,
              saldo: res.content.saldo || 0,
              nuevo_monto_capital: res.content.nuevo_monto_capital || 'No Capturado',
              nuevo_monto_intereses: res.content.nuevo_monto_intereses || 'No Capturado'
            }, { emitEvent: false });
          }
        },
        error => this.helperService.httpGlobalError(error)
      );
    }
  }

  private calcularNuevosMontos(monto: number): void {
    const tipoMovCtrl = this.movimientoForm.get('tipo_movimiento');
    const fechaCtrl = this.movimientoForm.get('fecha');
    const saldoCtrl = this.movimientoForm.get('saldo');

    const dataCalculo = {
      tipo_movimiento: tipoMovCtrl ? tipoMovCtrl.value : null,
      fecha: fechaCtrl ? fechaCtrl.value : null,
      monto: monto && !isNaN(monto) ? monto : 0,
      saldo_actual: saldoCtrl ? saldoCtrl.value : null
    };

    this.inversionistasService.calcularNuevosMontosPagare(this.idPagare, dataCalculo).pipe(
      takeUntil(this.destroy$)
    ).subscribe(
      (response: any) => {
        const res = response.json ? response.json() : response;
        if (res && res.success && res.content) {
          // Usamos operadores tradicionales (||) para evitar problemas de compatibilidad
          const nuevoCapital = res.content.nuevo_monto_capital !== undefined ? res.content.nuevo_monto_capital : (res.content.nuevoMontoCapital !== undefined ? res.content.nuevoMontoCapital : 0);
          const nuevoIntereses = res.content.nuevo_monto_intereses !== undefined ? res.content.nuevo_monto_intereses : (res.content.nuevoMontoIntereses !== undefined ? res.content.nuevoMontoIntereses : 0);

          this.movimientoForm.patchValue({
            nuevo_monto_capital: nuevoCapital,
            nuevo_monto_intereses: nuevoIntereses
          }, { emitEvent: false });
        }
      },
      error => this.helperService.httpGlobalError(error)
    );
  }

  public crearMovimiento(): void {
    this.movimientoForm.patchValue({
      folio: this.pagare.folio || '',
      divisa: this.pagare.divisa || 'MXN',
      inversionista: this.pagare.inversionista_nombre || '',
      tipo_movimiento: '',
      fecha: '',
      intereses: '',
      saldo: '',
      monto: '',
      programar_movimiento: false,
      nuevo_monto_capital: 'No Capturado',
      nuevo_monto_intereses: 'No Capturado'
    });

    $('#modalCrearMovimiento').modal('show');
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      if ((control as any).controls) {
        this.markFormGroupTouched(control as FormGroup);
      }
    });
  }

  // 1. Agrega esta variable en tu clase
public isSaving: boolean = false;

public guardarMovimiento(): void {
    if (this.movimientoForm.invalid) {
        this.markFormGroupTouched(this.movimientoForm);
        return;
    }

    // Activar estado de carga para bloquear el botón
    this.isSaving = true;
    const datosMovimiento = this.movimientoForm.getRawValue();

    this.inversionistasService.guardarMovimiento(this.idPagare, datosMovimiento).pipe(
        takeUntil(this.destroy$)
    ).subscribe(
        (response: any) => {
            this.isSaving = false; // Desactivar carga
            const res = response.json ? response.json() : response;
            
            if (res.success) {
                // Notificación opcional (si usas SweetAlert, Toastr, etc.)
                // this.helperService.showSuccess('Movimiento guardado exitosamente'); 
                
                $('#modalCrearMovimiento').modal('hide');
                this.movimientoForm.reset(); // Limpiar el formulario
                this.getDetallePagare(this.idPagare);
            }
        },
        error => {
            this.isSaving = false; // Desactivar carga incluso si hay error
            this.helperService.httpGlobalError(error);
        }
    );
}
}


// WEBPACK FOOTER //
// ./src/app/inversiones/pagares/detalles/pagare-detalles.component.ts